import React, { useState, useEffect, useRef } from "react";
import { 
  Calendar, Plus, Edit2, Trash2, Settings, LogOut, Users, Eye, 
  Upload, Image, Globe, Instagram, Facebook, Twitter, Loader2,
  Save, X, BarChart3, QrCode
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { OrganizerTicketScanner } from "./OrganizerTicketScanner";
import { OrganizerEventsTab } from "./OrganizerEventsTab";
import { OrganizerTeamTab } from "./OrganizerTeamTab";
import { OrganizerProfileTab } from "./OrganizerProfileTab";
import { OrganizerAnalyticsTab } from "./OrganizerAnalyticsTab";
import { OrganizerClientsTab } from "./OrganizerClientsTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OrganizerDashboard = ({ isOpen, onClose, organizer, onLogout, onUpdate }) => {
  const [activeTab, setActiveTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [stockValue, setStockValue] = useState("");
  
  const [profile, setProfile] = useState({
    company_name: organizer?.company_name || "",
    description: organizer?.description || "",
    logo: organizer?.logo || "",
    banner: organizer?.banner || "",
    website: organizer?.website || "",
    instagram: organizer?.instagram || "",
    facebook: organizer?.facebook || "",
    twitter: organizer?.twitter || "",
    theme_color: organizer?.theme_color || "#b3b3b3",
  });

  const [eventForm, setEventForm] = useState({
    title: "",
    artist: "",
    date: "",
    time: "",
    venue: "",
    location: "",
    price: "",
    available_tickets: "100",
    max_per_order: "10",
    image: "",
    category: "Hip-Hop/RNB",
    description: "",
    conditions: "",
    ticket_types: [
      { name: "Standard", price: "", quantity: "100", description: "" }
    ],
    useMultiTarif: false,
  });

  const [staffList, setStaffList] = useState([]);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [staffForm, setStaffForm] = useState({ email: "", name: "", role: "scanner" });
  const [showCancelConfirm, setShowCancelConfirm] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [showClaimModal, setShowClaimModal] = useState(false);

  const logoRef = useRef(null);
  const bannerRef = useRef(null);
  const eventImageRef = useRef(null);

  useEffect(() => {
    if (isOpen && organizer) {
      fetchEvents();
      fetchStaff();
      fetchAvailableEvents();
      setProfile({
        company_name: organizer.company_name || "",
        description: organizer.description || "",
        logo: organizer.logo || "",
        banner: organizer.banner || "",
        website: organizer.website || "",
        instagram: organizer.instagram || "",
        facebook: organizer.facebook || "",
        twitter: organizer.twitter || "",
        theme_color: organizer.theme_color || "#b3b3b3",
      });
    }
  }, [isOpen, organizer]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("tika_organizer_token");
      const response = await axios.get(`${API}/organizer/my-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data || []);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors du chargement des événements");
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem("tika_organizer_token");
      const response = await axios.get(`${API}/organizer/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffList(response.data || []);
    } catch (error) {
      console.error("Erreur staff:", error);
    }
  };

  const fetchAvailableEvents = async () => {
    try {
      const token = localStorage.getItem("tika_organizer_token");
      const response = await axios.get(`${API}/organizer/available-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableEvents(response.data || []);
    } catch (error) {
      console.error("Erreur available events:", error);
    }
  };

  const handleClaimEvent = async (eventId) => {
    try {
      const token = localStorage.getItem("tika_organizer_token");
      const response = await axios.post(`${API}/organizer/claim-event/${eventId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowClaimModal(false);
      fetchEvents();
      fetchAvailableEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la récupération");
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("tika_organizer_token");
      const response = await axios.post(`${API}/organizer/staff`, staffForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Membre ajouté ! Mot de passe temporaire : ${response.data.temp_password}`);
      setShowStaffForm(false);
      setStaffForm({ email: "", name: "", role: "scanner" });
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleRemoveStaff = async (staffId) => {
    try {
      const token = localStorage.getItem("tika_organizer_token");
      await axios.delete(`${API}/organizer/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Membre supprimé");
      fetchStaff();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleCancelEvent = async (eventId) => {
    try {
      const token = localStorage.getItem("tika_organizer_token");
      const response = await axios.post(`${API}/organizer/events/${eventId}/cancel`, 
        { reason: "Événement annulé par l'organisateur" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowCancelConfirm(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
    }
  };

  const addTicketType = () => {
    setEventForm({
      ...eventForm,
      ticket_types: [
        ...eventForm.ticket_types,
        { name: "", price: "", quantity: "50", description: "" }
      ]
    });
  };

  const removeTicketType = (index) => {
    if (eventForm.ticket_types.length > 1) {
      const newTypes = eventForm.ticket_types.filter((_, i) => i !== index);
      setEventForm({ ...eventForm, ticket_types: newTypes });
    }
  };

  const updateTicketType = (index, field, value) => {
    const newTypes = [...eventForm.ticket_types];
    newTypes[index] = { ...newTypes[index], [field]: value };
    setEventForm({ ...eventForm, ticket_types: newTypes });
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.url) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        if (type === "logo") {
          setProfile({ ...profile, logo: imageUrl });
        } else if (type === "banner") {
          setProfile({ ...profile, banner: imageUrl });
        } else if (type === "event") {
          setEventForm({ ...eventForm, image: imageUrl });
        }
        toast.success("Image uploadée !");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("tika_organizer_token");
      await axios.put(`${API}/organizer/me`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedOrganizer = { ...organizer, ...profile };
      localStorage.setItem("tika_organizer", JSON.stringify(updatedOrganizer));
      if (onUpdate) onUpdate(updatedOrganizer);
      
      toast.success("Profil mis à jour !");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleUpdateStock = async (eventId) => {
    try {
      const token = localStorage.getItem("tika_organizer_token");
      const newStock = parseInt(stockValue) || 0;
      
      await axios.put(`${API}/organizer/events/${eventId}/stock`, 
        { available_tickets: newStock },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Stock mis à jour !");
      setEditingStock(null);
      setStockValue("");
      fetchEvents();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la mise à jour du stock");
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    
    if (!eventForm.title || !eventForm.artist || !eventForm.date || !eventForm.venue || !eventForm.price) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      const token = localStorage.getItem("tika_organizer_token");
      const headers = { Authorization: `Bearer ${token}` };

      const eventData = {
        ...eventForm,
        price: parseFloat(eventForm.price),
        available_tickets: parseInt(eventForm.available_tickets) || 100,
        max_per_order: parseInt(eventForm.max_per_order) || 10,
        image: eventForm.image || "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae",
        ticket_types: eventForm.useMultiTarif ? eventForm.ticket_types.map(tt => ({
          name: tt.name,
          price: parseFloat(tt.price) || 0,
          quantity: parseInt(tt.quantity) || 0,
          description: tt.description || ""
        })) : null,
      };

      if (editingEvent) {
        await axios.put(`${API}/organizer/events/${editingEvent.id}`, eventData, { headers });
        toast.success("Événement mis à jour !");
      } else {
        await axios.post(`${API}/organizer/events`, eventData, { headers });
        toast.success("Événement créé !");
      }

      setShowEventForm(false);
      setEditingEvent(null);
      setEventForm({
        title: "", artist: "", date: "", time: "", venue: "",
        location: "", price: "", available_tickets: "100", max_per_order: "10", image: "", category: "Hip-Hop/RNB", description: "",
        conditions: "",
        ticket_types: [{ name: "Standard", price: "", quantity: "100", description: "" }],
        useMultiTarif: false,
      });
      fetchEvents();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      artist: event.artist,
      date: event.date,
      time: event.time || "",
      venue: event.venue,
      location: event.location || "",
      price: event.price?.toString() || "",
      available_tickets: event.available_tickets?.toString() || "100",
      max_per_order: event.max_per_order?.toString() || "10",
      image: event.image || "",
      category: event.category || "Hip-Hop/RNB",
      description: event.description || "",
      ticket_types: event.ticket_types && event.ticket_types.length > 0
        ? event.ticket_types.map(tt => ({
            name: tt.name || "",
            price: tt.price?.toString() || "",
            quantity: tt.quantity?.toString() || "",
            description: tt.description || ""
          }))
        : [{ name: "Standard", price: "", quantity: "100", description: "" }],
      useMultiTarif: !!event.ticket_types && (event.ticket_types.length > 1 || (event.ticket_types.length === 1 && event.ticket_types[0].name !== "Standard")),
    });
    setShowEventForm(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Supprimer cet événement ?")) return;

    try {
      const token = localStorage.getItem("tika_organizer_token");
      await axios.delete(`${API}/organizer/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Événement supprimé");
      fetchEvents();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("tika_organizer_token");
    localStorage.removeItem("tika_organizer");
    toast.success("Déconnexion réussie");
    onLogout();
    onClose();
  };

  const publicPageUrl = `${window.location.origin}/organizer/${organizer?.id}`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" aria-describedby="organizer-dashboard-description">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold">
              Mon espace organisateur
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
          <p id="organizer-dashboard-description" className="sr-only">Gérez vos événements et personnalisez votre page publique</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="glass-card rounded-lg p-4 text-center">
              <Calendar className="w-6 h-6 mx-auto mb-2 text-primary" />
              <div className="text-2xl font-bold">{events.length}</div>
              <div className="text-xs text-muted-foreground">Événements</div>
            </div>
            <div className="glass-card rounded-lg p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
              <div className="text-2xl font-bold">{organizer?.followers_count || 0}</div>
              <div className="text-xs text-muted-foreground">Abonnés</div>
            </div>
            <div className="glass-card rounded-lg p-4 text-center">
              <Eye className="w-6 h-6 mx-auto mb-2 text-primary" />
              <a 
                href={publicPageUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                Voir ma page
              </a>
              <div className="text-xs text-muted-foreground">Page publique</div>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-6 mb-4">
              <TabsTrigger value="events">
                <Calendar className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Événements</span>
                <span className="sm:hidden text-xs">Évén.</span>
              </TabsTrigger>
              <TabsTrigger value="clients" data-testid="organizer-clients-tab-trigger">
                <Users className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Clients</span>
                <span className="sm:hidden text-xs">Clients</span>
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="organizer-analytics-tab-trigger">
                <BarChart3 className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Analytics</span>
                <span className="sm:hidden text-xs">Stats</span>
              </TabsTrigger>
              <TabsTrigger value="scanner">
                <QrCode className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Scanner</span>
                <span className="sm:hidden text-xs">Scan</span>
              </TabsTrigger>
              <TabsTrigger value="team">
                <Users className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Équipe</span>
                <span className="sm:hidden text-xs">Team</span>
              </TabsTrigger>
              <TabsTrigger value="profile">
                <Settings className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Ma page</span>
                <span className="sm:hidden text-xs">Page</span>
              </TabsTrigger>
            </TabsList>

            {/* Events Tab */}
            <TabsContent value="events">
              <OrganizerEventsTab
                events={events}
                loading={loading}
                showEventForm={showEventForm}
                setShowEventForm={setShowEventForm}
                editingEvent={editingEvent}
                setEditingEvent={setEditingEvent}
                eventForm={eventForm}
                setEventForm={setEventForm}
                handleEventSubmit={handleEventSubmit}
                handleEditEvent={handleEditEvent}
                handleDeleteEvent={handleDeleteEvent}
                handleImageUpload={handleImageUpload}
                eventImageRef={eventImageRef}
                availableEvents={availableEvents}
                showClaimModal={showClaimModal}
                setShowClaimModal={setShowClaimModal}
                handleClaimEvent={handleClaimEvent}
                editingStock={editingStock}
                setEditingStock={setEditingStock}
                stockValue={stockValue}
                setStockValue={setStockValue}
                handleUpdateStock={handleUpdateStock}
                showCancelConfirm={showCancelConfirm}
                setShowCancelConfirm={setShowCancelConfirm}
                handleCancelEvent={handleCancelEvent}
                addTicketType={addTicketType}
                removeTicketType={removeTicketType}
                updateTicketType={updateTicketType}
              />
            </TabsContent>

            {/* Clients Tab */}
            <TabsContent value="clients">
              <OrganizerClientsTab />
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <OrganizerAnalyticsTab />
            </TabsContent>

            {/* Scanner Tab */}
            <TabsContent value="scanner">
              <OrganizerTicketScanner />
            </TabsContent>

            {/* Team Tab */}
            <TabsContent value="team">
              <OrganizerTeamTab
                staffList={staffList}
                showStaffForm={showStaffForm}
                setShowStaffForm={setShowStaffForm}
                staffForm={staffForm}
                setStaffForm={setStaffForm}
                handleAddStaff={handleAddStaff}
                handleRemoveStaff={handleRemoveStaff}
              />
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <OrganizerProfileTab
                profile={profile}
                setProfile={setProfile}
                handleImageUpload={handleImageUpload}
                handleSaveProfile={handleSaveProfile}
                uploading={uploading}
                logoRef={logoRef}
                bannerRef={bannerRef}
              />
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};
